import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import BuyerForm from './BuyerForm';
import ShippingSelector from './ShippingSelector';
import { showError } from '../../utils/sweetAlert';
import { getDisplayImageUrl } from '../../utils/imageUtils';

const getImageUrl = (imageUrl) => getDisplayImageUrl(imageUrl);

const Checkout = () => {
  const cart = useSelector((state) => state.cart.items);
  const subtotal = useSelector((state) => state.cart.total);
  const navigate = useNavigate();

  // Estado para almacenar los datos del comprador desde BuyerForm
  const [buyerData, setBuyerData] = useState({
    nombre: '',
    mail: '',
    cedula: '',
    direccion: '',
    ciudad: '',
    departamento: '',
    codigoPostal: '',
    telefono: '',
    referencias: '',
    marketingOptIn: false
  });

  // Estado para el tipo de envío
  const [shippingOption, setShippingOption] = useState({
    type: 'retiro_local',
    label: 'Retiro en pickup',
    cost: 0,
    estimatedDays: 'Disponible en 24hs'
  });

  // Estado para notas adicionales
  const [notes, setNotes] = useState('');

  // Estado para errores de validación del formulario
  const [errors, setErrors] = useState({});

  // Calcular total con envío
  const total = useMemo(() => {
    return subtotal + (shippingOption?.cost || 0);
  }, [subtotal, shippingOption]);

  const handleConfirmOrder = async () => {
    const { nombre, mail, cedula, direccion, ciudad, departamento, codigoPostal, telefono, referencias, marketingOptIn } = buyerData;
    
    const newErrors = {};
    let errorMessage = '';

    // Validar campos obligatorios
    if (!nombre) newErrors.nombre = true;
    if (!mail) newErrors.mail = true;
    if (!cedula) newErrors.cedula = true;

    // Validar correo con una expresión regular
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (mail && !emailRegex.test(mail)) {
      newErrors.mail = true;
      if (!errorMessage) errorMessage = 'Por favor, ingrese un correo electrónico válido.';
    }

    // Validar cédula (ejemplo: debe ser numérica y tener entre 7 y 10 dígitos)
    const cedulaRegex = /^\d{7,10}$/;
    if (cedula && !cedulaRegex.test(cedula)) {
      newErrors.cedula = true;
      if (!errorMessage) errorMessage = 'Por favor, ingrese una cédula válida (entre 7 y 10 dígitos).';
    }

    // Validar campos de dirección (excepto si es retiro en local)
    if (shippingOption.type !== 'retiro_local') {
      if (!direccion) newErrors.direccion = true;
      if (!ciudad) newErrors.ciudad = true;
      if (!telefono) newErrors.telefono = true;
    } else {
      // Para retiro en local solo necesitamos teléfono
      if (!shippingOption.pickupLocation?.id) newErrors.pickupLocation = true;
      if (!telefono) newErrors.telefono = true;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      
      if (!errorMessage) {
        if (newErrors.nombre || (!mail && newErrors.mail) || (!cedula && newErrors.cedula)) {
          errorMessage = 'Por favor, complete todos los datos personales.';
        } else if (newErrors.pickupLocation) {
          errorMessage = 'Por favor, seleccioná el punto de pickup donde vas a retirar tu pedido.';
        } else if (newErrors.direccion || newErrors.ciudad || newErrors.telefono) {
          if (shippingOption.type !== 'retiro_local') {
            errorMessage = 'Por favor, complete todos los campos de dirección de envío.';
          } else {
            errorMessage = 'Por favor, ingrese un teléfono de contacto.';
          }
        }
      }
      
      showError(errorMessage);
      return;
    }
    
    setErrors({});

    try {
      // Preparar items del carrito para enviar
      const items = cart.map(item => {
        const mapped = {
          _id: item._id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          selectedVariant: item.selectedVariant,
          images: item.images,
          image: item.image
        };
        // Incluir datos de personalización si es un producto personalizable
        if (item.isCustomizable) {
          mapped.isCustomizable = true;
          mapped.customizableProductId = item.customizableProductId || item._id;
          mapped.customImage = item.customImage;
          mapped.customFieldValues = item.customFieldValues;
          mapped.instructions = item.instructions;
          mapped.selectedSize = item.selectedSize;
        }
        return mapped;
      });

      const preferenceData = {
        items,
        subtotal,
        shippingType: shippingOption.type,
        shippingCost: shippingOption.cost,
        pickupLocation: shippingOption.pickupLocation?.id || '',
        total,
        nombre,
        mail,
        cedula,
        direccion: shippingOption.type === 'retiro_local' ? 'Retiro en pickup' : direccion,
        ciudad: shippingOption.pickupLocation?.city || ciudad,
        departamento: shippingOption.pickupLocation?.department || departamento,
        codigoPostal,
        telefono,
        referencias,
        notes,
        marketingOptIn
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/mp/create_preference`,
        preferenceData
      );

      if (response.data.init_point) {
        window.location.href = response.data.init_point;
      } else {
        showError("Error al procesar el pago");
      }
    } catch (error) {
      showError(error.response?.data?.error || 'Ocurrió un error al procesar el pago');
    }
  };

  const handleBackToCart = () => {
    navigate('/cart');
  };

  return (
    <div className="container wrapper mt-5">
      {cart.length === 0 ? (
        <div className="checkout-empty">
          <div className="text-center py-5">
            <i className="fa-solid fa-cart-shopping" style={{ fontSize: '4rem', color: '#ccc', marginBottom: '20px' }}></i>
            <h2 className="mb-3">No hay productos en tu carrito</h2>
            <p className="text-muted mb-4">Agrega algunos productos antes de proceder al checkout</p>
            <button 
              className="add-to-cart-btn px-5 py-3 shadow-sm"
              style={{ fontSize: '1rem', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: 'bold' }}
              onClick={() => navigate('/tienda')}
            >
              <i className="fa-solid fa-store me-2"></i>
              Ir a la tienda
            </button>
          </div>
        </div>
      ) : (
        <div className="checkout-container">
          {/* Header */}
          <div className="checkout-header">
            <div className="row align-items-center">
              <div className="col-md-6">
                <h1 className="checkout-title">
                  <i className="fa-solid fa-credit-card me-3"></i>
                  Finalizar Compra
                </h1>
                <p className="checkout-subtitle">
                  Revisa tu pedido y completa tus datos
                </p>
              </div>
              <div className="col-md-6 text-md-end">
                <div className="checkout-progress">
                  <span className="progress-step active">1. Revisión</span>
                  <span className="progress-arrow">→</span>
                  <span className="progress-step active">2. Datos</span>
                  <span className="progress-arrow">→</span>
                  <span className="progress-step">3. Pago</span>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            {/* Order Summary */}
            <div className="col-lg-7">
              <div className="checkout-section">
                <div className="section-header">
                  <h4>
                    <i className="fa-solid fa-list me-2"></i>
                    Resumen del Pedido
                  </h4>
                  <span className="item-count">
                    {cart.reduce((acc, item) => acc + item.quantity, 0)} productos
                  </span>
                </div>
                
                <div className="order-items">
                  {cart.map((item) => (
                    <div key={item.cartItemId || `${item._id}-${item.selectedVariant?.sku || 'no-variant'}`} className="checkout-item">
                      <div className="row align-items-center">
                        <div className="col-2">
                          <img 
                            src={getImageUrl(item.selectedVariant?.images?.[0] || item.images?.[0] || item.image)} 
                            alt={item.name}
                            className="checkout-item-image"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/80?text=Sin+Imagen';
                            }}
                          />
                        </div>
                        <div className="col-6">
                          <h6 className="item-name">{item.name}</h6>
                          {item.isCustomizable && (
                            <div className="mb-1">
                              <span className="badge" style={{ background: '#1f8e38', color: '#fff', fontSize: '0.65rem' }}>
                                <i className="fas fa-wand-magic-sparkles me-1"></i>Personalizado IA
                              </span>
                              {item.selectedSize && (
                                <span className="badge bg-light text-dark border ms-1" style={{ fontSize: '0.65rem' }}>{item.selectedSize}</span>
                              )}
                            </div>
                          )}
                          {item.isCustomizable && item.customImage?.url && (
                            <div className="d-flex align-items-center gap-1 mb-1">
                              <img src={getImageUrl(item.customImage.url)} alt="Foto" 
                                style={{ width: '24px', height: '24px', borderRadius: '4px', objectFit: 'cover', border: '1px solid #1f8e38' }}
                                onError={(e) => { e.target.style.display = 'none'; }} />
                              <small style={{ fontSize: '0.7rem', color: '#666' }}>Foto adjunta</small>
                            </div>
                          )}
                          {!item.isCustomizable && item.selectedVariant && (
                            <div className="item-variants">
                              <span className="variant-tag size">{item.selectedVariant.size}</span>
                              <span className="variant-tag color">{item.selectedVariant.color}</span>
                            </div>
                          )}
                          {!item.isCustomizable && item.selectedVariant?.sku && (
                            <small className="item-sku">SKU: {item.selectedVariant.sku}</small>
                          )}
                        </div>
                        <div className="col-2 text-center">
                          <span className="item-quantity">×{item.quantity}</span>
                        </div>
                        <div className="col-2 text-end">
                          <span className="item-price">${item.price * item.quantity}</span>
                          <small className="item-unit-price">${item.price} c/u</small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Shipping Selector */}
              <div className="checkout-section">
                <div className="section-header">
                  <h4>
                    <i className="fa-solid fa-truck me-2"></i>
                    Método de Envío
                  </h4>
                </div>
                <ShippingSelector 
                  selectedShipping={shippingOption.type}
                  selectedPickup={shippingOption.pickupLocation?.id}
                  onShippingChange={(option) => {
                    setShippingOption(option);
                    if (option.pickupLocation && errors.pickupLocation) {
                      setErrors(previous => ({ ...previous, pickupLocation: false }));
                    }
                  }}
                  ciudad={buyerData.ciudad}
                  pickupError={errors.pickupLocation}
                />
              </div>

              {/* Buyer Form */}
              <div className="checkout-section">
                <div className="section-header">
                  <h4>
                    <i className="fa-solid fa-user me-2"></i>
                    Datos del Comprador
                  </h4>
                </div>
                <BuyerForm 
                  onChange={(data) => {
                    setBuyerData(data);
                    if (Object.keys(errors).length > 0) {
                      setErrors({});
                    }
                  }} 
                  shippingType={shippingOption.type}
                  pickupLocation={shippingOption.pickupLocation}
                  errors={errors}
                />
              </div>

              {/* Notas adicionales */}
              <div className="checkout-section">
                <div className="section-header">
                  <h4>
                    <i className="fa-solid fa-comment me-2"></i>
                    Notas del Pedido (opcional)
                  </h4>
                </div>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Instrucciones especiales, horarios de entrega preferidos, etc."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>

            {/* Payment Summary */}
            <div className="col-lg-5">
              <div className="checkout-sidebar">
                <div className="payment-summary">
                  <div className="summary-header">
                    <h4>
                      <i className="fa-solid fa-calculator me-2"></i>
                      Total del Pedido
                    </h4>
                  </div>
                  
                  <div className="summary-content">
                    <div className="summary-row">
                      <span>Subtotal ({cart.reduce((acc, item) => acc + item.quantity, 0)} productos)</span>
                      <span>${subtotal}</span>
                    </div>
                    <div className="summary-row">
                      <span>
                        <i className="fa-solid fa-truck me-1"></i>
                        {shippingOption.label}
                      </span>
                      {shippingOption.cost === 0 ? (
                        <span className="free-text">Gratis</span>
                      ) : (
                        <span>${shippingOption.cost}</span>
                      )}
                    </div>
                    <div className="summary-row shipping-time">
                      <small className="text-muted">
                        <i className="fa-regular fa-clock me-1"></i>
                        {shippingOption.estimatedDays}
                      </small>
                    </div>
                    <div className="summary-row">
                      <span>Impuestos</span>
                      <span>Incluidos</span>
                    </div>
                    <div className="summary-divider"></div>
                    <div className="summary-row total">
                      <strong>Total a Pagar</strong>
                      <strong className="total-amount">${total}</strong>
                    </div>
                  </div>

                  <div className="payment-actions">
                    <button 
                      className="add-to-cart-btn w-100 py-3 shadow-sm mb-3"
                      style={{ fontSize: '1rem', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: 'bold' }}
                      onClick={handleConfirmOrder}
                    >
                      <i className="fa-solid fa-credit-card me-2"></i>
                      PAGAR AHORA
                    </button>
                    
                    <button 
                      className="btn btn-outline-secondary w-100 py-2"
                      onClick={handleBackToCart}
                    >
                      <i className="fa-solid fa-arrow-left me-2"></i>
                      Volver al Carrito
                    </button>
                  </div>

                  <div className="security-badges">
                    <div className="security-item">
                      <i className="fa-solid fa-shield-halved"></i>
                      <span>Compra Segura</span>
                    </div>
                    <div className="security-item">
                      <i className="fa-solid fa-credit-card"></i>
                      <span>Mercado Pago</span>
                    </div>
                    <div className="security-item">
                      <i className="fa-solid fa-undo"></i>
                      <span>Garantía de devolución</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkout;
